import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  Accessibility,
  ArrowDown,
  ArrowUp,
  BatteryCharging,
  Lock,
  LockOpen,
  Monitor,
  MonitorOff,
  Search,
  Send,
  Smartphone,
} from "lucide-react";
import { PageHeader } from "@/components/PageHeader";
import { QueryErrorCard } from "@/components/QueryErrorCard";
import { StaleDataMarker } from "@/components/ConnectionBadge";
import { Guarded } from "@/components/Guarded";
import { EmptyState } from "@/components/fleet/EmptyState";
import { DeviceHealthPill } from "@/components/fleet/DeviceHealthPill";
import { BulkActionBar } from "@/components/fleet/BulkActionBar";
import { Button } from "@/components/ui/Button";
import { Checkbox } from "@/components/ui/Checkbox";
import { Select } from "@/components/ui/Select";
import { Skeleton } from "@/components/ui/Skeleton";
import { Tooltip } from "@/components/ui/Tooltip";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeaderCell,
  TableRow,
} from "@/components/ui/Table";
import { useNow } from "@/components/ClockProvider";
import { useKeysetQuery } from "@/hooks/useKeyset";
import { useRowNavigation } from "@/hooks/useRowNavigation";
import { qs } from "@/lib/api";
import { S, T } from "@/lib/strings";
import { cn, timeAgo } from "@/lib/utils";
import {
  deviceFilterFromParams,
  isDeviceFilterEmpty,
  paramsFromDeviceFilter,
  type DevicesUrlFilter,
} from "@/lib/runFilters";
import type { DeviceHealth, DeviceSummary, Selection } from "@/types/fleet";

/**
 * 100 per page, so a ~100-device fleet arrives in one request.
 *
 * This is not arbitrary: with everything loaded, "選取全部符合條件的 N 部" can
 * state a real N. There is no count endpoint in the API — deliberately, counting
 * behind a filter is the expensive half — so beyond one page the console would
 * be guessing, and it says so instead.
 */
const PAGE_SIZE = 100;

type SortKey = "label" | "last_seen_at" | "battery_pct" | "health";

const HEALTH_OPTIONS: Array<{ value: DeviceHealth | ""; label: string }> = [
  { value: "", label: S.filterHealth },
  { value: "online", label: "online" },
  { value: "stale", label: "stale" },
  { value: "offline", label: "offline" },
  { value: "unseen", label: "unseen" },
];

export function Devices() {
  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();
  const now = useNow();
  const searchRef = useRef<HTMLInputElement>(null);

  const filter = useMemo(() => deviceFilterFromParams(params), [params]);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(() => new Set());
  const [filterSelection, setFilterSelection] = useState<DevicesUrlFilter | null>(null);
  // The search box is local and lands in the URL 250 ms later: pushing every
  // keystroke straight into the query string would mean a new query key — and a
  // new request — per character typed.
  const [term, setTerm] = useState(filter.q ?? "");

  const sort = filter.sort ?? "";
  const [sortKey, sortDir] = parseSort(sort);

  const query = useKeysetQuery<DeviceSummary>(
    ["devices", "list", filter],
    (cursor) =>
      `/devices${qs({
        q: filter.q,
        health: filter.health,
        tag: filter.tag,
        group_id: filter.group_id,
        has_run: filter.has_run,
        sort: filter.sort,
        limit: PAGE_SIZE,
        cursor,
      })}`,
  );

  // `app_version_name` is not in rest-api.md's documented query list for
  // /devices, so it is applied client-side over the loaded pages rather than
  // silently sent as a parameter the server may ignore. Flagged in the handover.
  const rows = useMemo(() => {
    let out = query.items;
    if (filter.app_version_name) {
      out = out.filter((d) => d.app_version_name === filter.app_version_name);
    }
    return out;
  }, [query.items, filter.app_version_name]);

  const versions = useMemo(() => {
    const set = new Set(query.items.map((d) => d.app_version_name).filter(Boolean));
    return [...set].sort();
  }, [query.items]);

  const tags = useMemo(() => {
    const set = new Set(query.items.flatMap((d) => d.tags));
    return [...set].sort();
  }, [query.items]);

  const patch = useCallback(
    (next: Partial<DevicesUrlFilter>) => {
      setParams(paramsFromDeviceFilter({ ...filter, ...next }), { replace: true });
      // A changed filter invalidates a selection that was described by the old
      // one; keeping it would act on rows the operator can no longer see.
      setSelectedIds(new Set());
      setFilterSelection(null);
    },
    [filter, setParams],
  );

  useEffect(() => {
    const current = filter.q ?? "";
    if (term === current) return;
    const id = window.setTimeout(() => patch({ q: term || undefined }), 250);
    return () => window.clearTimeout(id);
  }, [term, filter.q, patch]);

  const nav = useRowNavigation({
    items: rows,
    getKey: (d) => d.id,
    onOpen: (d) => navigate(`/devices/${d.id}`),
    searchRef,
  });

  const allOnPage = rows.length > 0 && rows.every((d) => selectedIds.has(d.id));
  const someOnPage = rows.some((d) => selectedIds.has(d.id));

  // A filter selection carries the descriptor, not the ids: `estimated` is the
  // best lower bound the client has (the rows it has loaded) and is labelled as
  // an estimate wherever it is shown. The authoritative number comes back from
  // the server's dispatch preview at confirm time.
  const selection: Selection | null = filterSelection
    ? { kind: "filter", filter: filterSelection, estimated: rows.length }
    : selectedIds.size > 0
      ? { kind: "ids", ids: [...selectedIds] }
      : null;

  // Unknown while pages remain unloaded — there is no count endpoint to ask.
  const matchingCount = query.canLoadMore ? null : rows.length;

  function toggleRow(id: string) {
    setFilterSelection(null);
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  function toggleAllOnPage(checked: boolean) {
    setFilterSelection(null);
    setSelectedIds((prev) => {
      const next = new Set(prev);
      for (const d of rows) {
        if (checked) next.add(d.id);
        else next.delete(d.id);
      }
      return next;
    });
  }

  function dispatchToSelection() {
    if (!selection) return;
    // The wizard takes explicit ids. A filter selection is handed over as the
    // ids currently known, and the server's dispatch preview is what the
    // operator actually confirms against.
    const ids = selection.kind === "ids" ? selection.ids : rows.map((d) => d.id);
    navigate(`/dispatch?devices=${encodeURIComponent(ids.join(","))}`);
  }

  return (
    <>
      <PageHeader
        title={S.devicesTitle}
        hint={
          <>
            {S.devicesHint}
            <StaleDataMarker className="ml-1" />
          </>
        }
      />

      {/* ---- filters ---- */}
      <div className="mb-3 flex flex-wrap items-center gap-2">
        <div className="flex min-w-[220px] items-center gap-1.5 rounded-md border border-[var(--color-border)] bg-[rgba(255,255,255,0.03)] px-2 py-1.5 focus-within:border-[var(--color-accent)]">
          <Search size={13} className="shrink-0 text-[var(--color-fg-subtle)]" />
          <input
            ref={searchRef}
            value={term}
            onChange={(e) => setTerm(e.target.value)}
            placeholder={S.filterSearch}
            aria-label={S.search}
            className="w-full bg-transparent text-[13px] placeholder:text-[var(--color-fg-faint)] focus:outline-none"
          />
          <kbd className="hidden shrink-0 rounded bg-[rgba(255,255,255,0.06)] px-1 font-mono text-[10px] text-[var(--color-fg-faint)] sm:block">
            /
          </kbd>
        </div>

        <Select
          size="sm"
          aria-label={S.filterHealth}
          value={filter.health ?? ""}
          onChange={(e) => patch({ health: (e.target.value || undefined) as DeviceHealth })}
          options={HEALTH_OPTIONS.filter((o) => o.value !== "")}
          placeholder={S.filterHealth}
        />

        <Select
          size="sm"
          aria-label={S.filterTag}
          value={filter.tag ?? ""}
          onChange={(e) => patch({ tag: e.target.value || undefined })}
          options={tags.map((t) => ({ value: t, label: t }))}
          placeholder={S.filterTag}
        />

        <Select
          size="sm"
          aria-label={S.filterVersion}
          value={filter.app_version_name ?? ""}
          onChange={(e) => patch({ app_version_name: e.target.value || undefined })}
          options={versions.map((v) => ({ value: v, label: v }))}
          placeholder={S.filterVersion}
        />

        <Select
          size="sm"
          aria-label={S.filterHasRun}
          value={filter.has_run === undefined ? "" : filter.has_run ? "1" : "0"}
          onChange={(e) =>
            patch({ has_run: e.target.value === "" ? undefined : e.target.value === "1" })
          }
          options={[
            { value: "1", label: S.filterHasRunYes },
            { value: "0", label: S.filterHasRunNo },
          ]}
          placeholder={S.filterHasRun}
        />

        {!isDeviceFilterEmpty(filter) && (
          <Button size="sm" variant="ghost" onClick={() => patch({ q: undefined, health: undefined, tag: undefined, group_id: undefined, app_version_name: undefined, has_run: undefined })}>
            {S.clearFilters}
          </Button>
        )}
      </div>

      {query.isError && (
        <QueryErrorCard error={query.error} onRetry={() => void query.refetch()} className="mb-3" />
      )}

      {/* ---- table ---- */}
      {query.isInitialLoading ? (
        <TableSkeleton />
      ) : rows.length === 0 ? (
        <EmptyState
          icon={Smartphone}
          title={isDeviceFilterEmpty(filter) ? "還沒有裝置" : "沒有符合的裝置"}
          body={isDeviceFilterEmpty(filter) ? S.noDevices : S.noDevicesFiltered}
          action={
            !isDeviceFilterEmpty(filter) ? (
              <Button size="sm" variant="default" onClick={() => setParams(new URLSearchParams())}>
                {S.clearFilters}
              </Button>
            ) : undefined
          }
        />
      ) : (
        <>
          <Table>
            <TableHead>
              <tr>
                <TableHeaderCell className="w-8">
                  <Checkbox
                    checked={allOnPage}
                    indeterminate={someOnPage}
                    onCheckedChange={toggleAllOnPage}
                    ariaLabel={S.colSelect}
                  />
                </TableHeaderCell>
                <SortableHeader
                  label={S.colDevice}
                  active={sortKey === "label"}
                  dir={sortDir}
                  onClick={() => patch({ sort: nextSort(sort, "label") })}
                />
                <SortableHeader
                  label={S.colHealth}
                  active={sortKey === "health"}
                  dir={sortDir}
                  onClick={() => patch({ sort: nextSort(sort, "health") })}
                />
                <SortableHeader
                  label={S.colBattery}
                  active={sortKey === "battery_pct"}
                  dir={sortDir}
                  onClick={() => patch({ sort: nextSort(sort, "battery_pct") })}
                  className="w-20"
                />
                <TableHeaderCell className="w-24">{S.colStatus}</TableHeaderCell>
                <TableHeaderCell>{S.colTags}</TableHeaderCell>
                <TableHeaderCell>{S.colCurrentRun}</TableHeaderCell>
                <TableHeaderCell className="w-24">{S.colToday}</TableHeaderCell>
                <TableHeaderCell className="w-24">{S.colAppVersion}</TableHeaderCell>
                <SortableHeader
                  label={S.colLastSeen}
                  active={sortKey === "last_seen_at"}
                  dir={sortDir}
                  onClick={() => patch({ sort: nextSort(sort, "last_seen_at") })}
                  className="w-28"
                />
              </tr>
            </TableHead>
            <TableBody>
              {rows.map((d, i) => (
                <TableRow
                  key={d.id}
                  ref={nav.registerRow(d.id)}
                  onClick={() => navigate(`/devices/${d.id}`)}
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") navigate(`/devices/${d.id}`);
                  }}
                  className={cn(
                    "cursor-pointer",
                    nav.isCursor(i) && "bg-[rgba(113,112,255,0.08)] outline outline-1 -outline-offset-1 outline-[var(--color-accent)]",
                    selectedIds.has(d.id) && "bg-[rgba(255,255,255,0.035)]",
                  )}
                >
                  <TableCell onClick={(e) => e.stopPropagation()}>
                    <Checkbox
                      checked={selectedIds.has(d.id)}
                      onCheckedChange={() => toggleRow(d.id)}
                      ariaLabel={`${S.colSelect} ${d.label}`}
                    />
                  </TableCell>

                  <TableCell>
                    <div className="min-w-0">
                      <div className="truncate text-[13px] font-medium text-[var(--color-fg)]">
                        {d.label}
                      </div>
                      <div className="truncate font-mono text-[10px] text-[var(--color-fg-faint)]">
                        {d.id}
                      </div>
                    </div>
                  </TableCell>

                  <TableCell>
                    <DeviceHealthPill device={d} size="sm" />
                  </TableCell>

                  <TableCell>
                    <BatteryCell pct={d.battery_pct} charging={d.charging} />
                  </TableCell>

                  <TableCell>
                    <StatusIcons device={d} />
                  </TableCell>

                  <TableCell>
                    <div className="flex flex-wrap gap-1">
                      {d.tags.slice(0, 3).map((t) => (
                        <span
                          key={t}
                          className="rounded bg-[rgba(255,255,255,0.06)] px-1.5 py-0.5 text-[10px] text-[var(--color-fg-muted)]"
                        >
                          {t}
                        </span>
                      ))}
                      {d.tags.length > 3 && (
                        <span className="text-[10px] text-[var(--color-fg-faint)]">
                          +{d.tags.length - 3}
                        </span>
                      )}
                    </div>
                  </TableCell>

                  <TableCell>
                    {d.active_run ? (
                      <Tooltip content={d.active_run.task_name}>
                        <span className="font-mono text-[11px] text-[#f59e0b]">
                          {T.stepProgress(d.active_run.n, d.active_run.max_steps)}
                        </span>
                      </Tooltip>
                    ) : (
                      <span className="text-[var(--color-fg-faint)]">—</span>
                    )}
                  </TableCell>

                  <TableCell>
                    <span className="font-mono text-[11px] tabular-nums">
                      <span className="text-[#10b981]">{d.today.success} ✓</span>
                      <span className="text-[var(--color-fg-faint)]"> / </span>
                      <span className={d.today.failed > 0 ? "text-[#ef4444]" : "text-[var(--color-fg-faint)]"}>
                        {d.today.failed} ✗
                      </span>
                    </span>
                  </TableCell>

                  <TableCell>
                    <span className="font-mono text-[11px]">{d.app_version_name}</span>
                  </TableCell>

                  <TableCell>
                    <span className="whitespace-nowrap text-[11px] text-[var(--color-fg-subtle)]">
                      {timeAgo(d.last_seen_at, now)}
                    </span>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>

          <div className="mt-3 flex items-center gap-2">
            {query.canLoadMore ? (
              <Button
                size="sm"
                variant="default"
                onClick={query.loadMore}
                disabled={query.isFetchingNextPage}
              >
                {query.isFetchingNextPage ? S.loading : S.loadMore}
              </Button>
            ) : (
              <span className="text-[11px] text-[var(--color-fg-faint)]">{S.noMore}</span>
            )}
          </div>

          <BulkActionBar
            selection={selection}
            pageCount={selectedIds.size}
            matchingCount={matchingCount}
            onSelectAllMatching={() => {
              setFilterSelection(filter);
              setSelectedIds(new Set(rows.map((d) => d.id)));
            }}
            onClear={() => {
              setSelectedIds(new Set());
              setFilterSelection(null);
            }}
          >
            <Guarded permission="dispatch_run">
              <Button size="sm" variant="primary" onClick={dispatchToSelection}>
                <Send size={12} />
                {S.bulkDispatch}
              </Button>
            </Guarded>
          </BulkActionBar>
        </>
      )}
    </>
  );
}

/* ------------------------------------------------------------------ *
 * Cells
 * ------------------------------------------------------------------ */

function BatteryCell({ pct, charging }: { pct: number | null; charging: boolean }) {
  if (pct === null || pct === undefined) return <span className="text-[var(--color-fg-faint)]">—</span>;
  const low = pct <= 20;
  return (
    <span
      className={cn(
        "inline-flex items-center gap-1 font-mono text-[11px] tabular-nums",
        low && !charging ? "text-[#ef4444]" : "text-[var(--color-fg-muted)]",
      )}
    >
      {charging && <BatteryCharging size={11} className="text-[#10b981]" />}
      {pct}%
    </span>
  );
}

/**
 * Screen / keyguard / accessibility, as three fixed slots.
 *
 * Fixed slots rather than "show what's on": the eye learns the positions and can
 * scan a column of eighty rows for the one that is dark in the third slot.
 * Greyed means off, and each has a tooltip plus an aria-label because an icon
 * alone is not a label.
 */
function StatusIcons({ device }: { device: DeviceSummary }) {
  const items = [
    {
      on: device.screen_on,
      OnIcon: Monitor,
      OffIcon: MonitorOff,
      onLabel: S.iconScreenOn,
      offLabel: S.iconScreenOff,
    },
    {
      on: !device.keyguard_locked,
      OnIcon: LockOpen,
      OffIcon: Lock,
      onLabel: S.iconKeyguardUnlocked,
      offLabel: S.iconKeyguardLocked,
    },
    {
      on: device.a11y_connected,
      OnIcon: Accessibility,
      OffIcon: Accessibility,
      onLabel: S.iconA11yOn,
      offLabel: S.iconA11yOff,
    },
  ];

  return (
    <div className="flex items-center gap-1.5">
      {items.map((it, i) => {
        const Icon = it.on ? it.OnIcon : it.OffIcon;
        const label = it.on ? it.onLabel : it.offLabel;
        return (
          <Tooltip key={i} content={label}>
            <span aria-label={label} role="img">
              <Icon
                size={12}
                className={it.on ? "text-[var(--color-fg-muted)]" : "text-[var(--color-fg-faint)] opacity-40"}
              />
            </span>
          </Tooltip>
        );
      })}
    </div>
  );
}

/* ------------------------------------------------------------------ *
 * Sorting
 * ------------------------------------------------------------------ */

function parseSort(sort: string): [SortKey | null, "asc" | "desc"] {
  if (!sort) return [null, "asc"];
  const desc = sort.startsWith("-");
  return [(desc ? sort.slice(1) : sort) as SortKey, desc ? "desc" : "asc"];
}

/** Click cycles asc → desc → off, which is what a header is expected to do. */
function nextSort(current: string, key: SortKey): string | undefined {
  if (current === key) return `-${key}`;
  if (current === `-${key}`) return undefined;
  return key;
}

function SortableHeader({
  label,
  active,
  dir,
  onClick,
  className,
}: {
  label: string;
  active: boolean;
  dir: "asc" | "desc";
  onClick: () => void;
  className?: string;
}) {
  return (
    <TableHeaderCell className={className}>
      <button
        type="button"
        onClick={onClick}
        aria-sort={active ? (dir === "asc" ? "ascending" : "descending") : "none"}
        className="inline-flex items-center gap-1 uppercase tracking-wider hover:text-[var(--color-fg)] focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-[var(--color-accent)]"
      >
        {label}
        {active &&
          (dir === "asc" ? <ArrowUp size={10} /> : <ArrowDown size={10} />)}
      </button>
    </TableHeaderCell>
  );
}

function TableSkeleton() {
  return (
    <div className="overflow-hidden rounded-lg border border-[var(--color-border-subtle)]">
      <div className="h-9 bg-black/20" />
      {Array.from({ length: 8 }, (_, i) => (
        <div
          key={i}
          className="flex items-center gap-3 border-b border-[var(--color-border-subtle)] px-3 py-2.5 last:border-b-0"
        >
          <Skeleton className="size-3.5" />
          <div className="flex-1 space-y-1">
            <Skeleton className="h-3 w-32" />
            <Skeleton className="h-2 w-40" />
          </div>
          <Skeleton className="h-4 w-14 rounded-full" />
          <Skeleton className="h-3 w-10" />
          <Skeleton className="h-3 w-16" />
          <Skeleton className="h-3 w-20" />
          <Skeleton className="h-3 w-14" />
        </div>
      ))}
    </div>
  );
}
